import { WEEKDAY_DATE_FORMAT } from "@frontend-shared/constants";
import type { TDateFnsLocaleKey } from "@frontend-shared/utils";
import {
  getDateFnsLocaleKey,
  getDateFormat,
  shouldUseNarrowWeekdayNames,
} from "@frontend-shared/utils";
import type { Locale } from "date-fns";
import { format } from "date-fns";
import { ar, enUS, ru } from "date-fns/locale";

import type { IFormatLocalizedDateProps } from "./date-locale.types";

// NOTE: The locale OBJECTS live per app — `frontend-shared` carries no date-fns
// dependency, and web/mobile are on different majors. Only the key mapping is
// shared (`getDateFnsLocaleKey`), so both platforms cannot drift apart on which
// locale a language means.
const DATE_FNS_LOCALES: Record<TDateFnsLocaleKey, Locale> = { ar, enUS, ru };

export const resolveDateFnsLocale = (language: string): Locale =>
  DATE_FNS_LOCALES[getDateFnsLocaleKey(language)];

// NOTE: The pattern alone is not enough. `getDateFormat` already picked
// `MMM d, yyyy` for Arabic, but with no locale handed to `format` the `MMM`
// token rendered the English "Jul" inside an otherwise Arabic screen
// (NSAI-4989). Mobile's `formatLocalizedDate` is the same two lines.
export const formatLocalizedDate = ({
  date,
  language,
}: IFormatLocalizedDateProps): string =>
  format(date, getDateFormat(language), {
    locale: resolveDateFnsLocale(language),
  });

// NOTE: NSAI-4801 — the weekday-leading form for the uploads/meeting rows. Both
// apps hardcoded the identical `EEEE, d MMM, yyyy` literal with no locale, so an
// Arabic UI read "Thursday, 6 Aug, 2026". The pattern now lives in
// `WEEKDAY_DATE_FORMAT` and mobile's `formatLocalizedWeekdayDate` is this same
// function, so the two rows cannot drift.
export const formatLocalizedWeekdayDate = ({
  date,
  language,
}: IFormatLocalizedDateProps): string =>
  format(date, WEEKDAY_DATE_FORMAT, {
    locale: resolveDateFnsLocale(language),
  });

const DAY_INDEXES = [0, 1, 2, 3, 4, 5, 6] as const;

// NOTE: The mirror of mobile's `buildDayNamesShort`. react-day-picker's default
// weekday formatter is `format(day, "cccccc")` — the short form — which for
// Arabic is six characters in a `w-9` cell. Both platforms apply the same
// "widest form that fits" rule from `shouldUseNarrowWeekdayNames`, so the two
// grids print the same header rather than each degrading its own way.
export const getWeekdayHeaderNames = (locale: Locale): string[] => {
  const shortNames = DAY_INDEXES.map((index) =>
    locale.localize.day(index, { width: "short" }),
  );

  if (!shouldUseNarrowWeekdayNames(shortNames)) return shortNames;

  return DAY_INDEXES.map((index) =>
    locale.localize.day(index, { width: "narrow" }),
  );
};
